import { useEffect, useRef, useState } from "react";
import { useAuth } from "react-oidc-context";
import { Spinner } from "../components/Filtering/Reusable/Spinner";
import { appConfig } from "../utils/config";
import {
  bootstrapAdminConnectorRouting,
  clearStoredAdminConnectorConfig,
  getDecodedParticipantId,
} from "../utils/auth/adminConnectorRuntime";
import { getTokenRoleSnapshot } from "../utils/auth/connectorRouting";

export function ProtectedRoute({ children }) {
  const { isAuthenticated, isLoading, signinRedirect, user } = useAuth();
  const [completedBootstrapIdentity, setCompletedBootstrapIdentity] =
    useState(null);
  const lastBootstrappedTokenRef = useRef("");
  const isInitialAuthLoading = isLoading && !isAuthenticated;
  const token = user?.access_token || user?.id_token || "";
  const idToken = user?.id_token || "";
  const snapshot = getTokenRoleSnapshot(token);
  const idTokenSnapshot =
    idToken && idToken !== token ? getTokenRoleSnapshot(idToken) : null;
  const participantId =
    getDecodedParticipantId(snapshot.decoded) ||
    getDecodedParticipantId(idTokenSnapshot?.decoded);
  const subject = snapshot.decoded?.sub || idTokenSnapshot?.decoded?.sub || "";
  const bootstrapIdentity = isAuthenticated
    ? `${snapshot.isAdmin ? "admin" : "user"}:${subject}:${participantId}`
    : "__empty__";

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      clearStoredAdminConnectorConfig();
      Promise.resolve(
        signinRedirect({
          extraQueryParams: {
            brand: appConfig.brandTheme,
          },
        }),
      ).catch(console.error);
    }
  }, [isLoading, isAuthenticated, signinRedirect]);

  useEffect(() => {
    if (!isAuthenticated) {
      setCompletedBootstrapIdentity(null);
      lastBootstrappedTokenRef.current = "";
      return;
    }

    if (!token) {
      return;
    }

    const isCompletedIdentity = completedBootstrapIdentity === bootstrapIdentity;
    if (isCompletedIdentity && lastBootstrappedTokenRef.current === token) {
      return;
    }

    let cancelled = false;

    if (!isCompletedIdentity) {
      setCompletedBootstrapIdentity(null);
    }

    lastBootstrappedTokenRef.current = token;

    Promise.resolve(
      bootstrapAdminConnectorRouting(token, {
        participantId,
      }),
    )
      .catch((error) => {
        console.error("Admin connector bootstrap failed", error);
      })
      .finally(() => {
        if (!cancelled && !isCompletedIdentity) {
          setCompletedBootstrapIdentity(bootstrapIdentity);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [
    bootstrapIdentity,
    completedBootstrapIdentity,
    isAuthenticated,
    isLoading,
    participantId,
    token,
  ]);

  if (
    isInitialAuthLoading ||
    (isAuthenticated && completedBootstrapIdentity !== bootstrapIdentity)
  ) {
    return (
      <div className="spinner-container">
        <Spinner size="large" color="var(--main-color)" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  return children;
}
